
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const useOrganizationStats = () => {
  const { currentOrganization, profile } = useAuth();

  return useQuery({
    queryKey: ['organization-stats', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization || !profile) {
        return { totalUsers: 0, totalClubs: 0, totalTeams: 0, totalPlayers: 0, activeSeasons: 0 };
      }

      // Get users in organization
      const { data: users, error: usersError } = await supabase
        .from('organization_memberships')
        .select('user_id')
        .eq('organization_id', currentOrganization.id)
        .eq('status', 'accepted');

      if (usersError) throw usersError;

      // Get clubs in organization
      const { data: clubs, error: clubsError } = await supabase
        .from('clubs')
        .select('id')
        .eq('organization_id', currentOrganization.id);

      if (clubsError) throw clubsError;

      // Get teams in organization clubs
      const clubIds = clubs?.map(club => club.id) || [];
      const { data: teams, error: teamsError } = await supabase
        .from('teams')
        .select('id')
        .in('club_id', clubIds.length > 0 ? clubIds : ['']);

      if (teamsError) throw teamsError;

      // Get players in organization teams
      const teamIds = teams?.map(team => team.id) || [];
      const { data: players, error: playersError } = await supabase
        .from('players')
        .select('id')
        .in('team_id', teamIds.length > 0 ? teamIds : ['']);

      if (playersError) throw playersError;

      // Get active seasons in organization clubs
      const { data: activeSeasons, error: seasonsError } = await supabase
        .from('seasons')
        .select('id')
        .in('club_id', clubIds.length > 0 ? clubIds : [''])
        .eq('status', 'active');

      if (seasonsError) throw seasonsError;

      return {
        totalUsers: users?.length || 0,
        totalClubs: clubs?.length || 0,
        totalTeams: teams?.length || 0,
        totalPlayers: players?.length || 0,
        activeSeasons: activeSeasons?.length || 0,
      };
    },
    enabled: !!currentOrganization && !!profile,
  });
};
